/**
 * Was die drei Lagerungsdichte-Stufen bewirken, abgeleitet aus der
 * Bodenartentabelle — der Tabelle, mit der die App den nutzbaren Wasserspeicher
 * wirklich rechnet ([nwsk.ts]).
 *
 * Die Zahlen sind der nutzbare Wasserspeicher je Dezimeter Lagemächtigkeit;
 * `schichtNWSKEval` nimmt sie als `(normal + Humuszuschlag) × Skelettabschlag
 * / 10 × Mächtigkeit`. Sie hier noch einmal abzuschreiben hiesse, eine zweite
 * Wahrheit anzulegen, die auseinanderläuft, sobald jemand die Liste in der
 * Listenverwaltung ändert.
 *
 * Viele Bodenarten teilen sich dieselben drei Werte (alle organischen
 * Substrate zum Beispiel). Deshalb wird nach dem Wertetripel gruppiert: eine
 * Zeile je Tripel, die Bodenarten dahinter. Eine Tabelle mit über fünfzig
 * Einzelzeilen liest im Gelände niemand.
 */
export interface DichteWirkung {
	gering: number;
	mittel: number;
	hoch: number;
	/** Bodenarten mit genau diesen drei Werten, in der Reihenfolge der Liste. */
	bodenarten: string[];
	/** Wahr, wenn die Stufe bei diesen Bodenarten nichts ändert. */
	ohneWirkung: boolean;
}

type Tabelle = Record<string, any>;

/** Liest die drei Werte einer Bodenart; fehlt einer, fällt die Art heraus. */
function tripel(eintrag: any): [number, number, number] | undefined {
	const l = eintrag?.['Lagerungsdichte'];
	if (!l) return undefined;
	const werte = [l['gering'], l['mittel'], l['hoch']];
	if (werte.some(w => typeof w !== 'number' || !isFinite(w))) return undefined;
	return werte as [number, number, number];
}

/**
 * Gruppiert die Bodenartentabelle nach gleichem Wertetripel. Sortiert nach dem
 * Wert für „gering" aufsteigend — so stehen die trockenen Sande oben und die
 * organischen Substrate unten, wie in der Substratgliederung.
 */
export function dichteWirkungen(tabelle: Tabelle): DichteWirkung[] {
	const gruppen = new Map<string, DichteWirkung>();
	for (const [name, eintrag] of Object.entries(tabelle ?? {})) {
		const t = tripel(eintrag);
		if (!t) continue;
		const schluessel = t.join('|');
		const vorhanden = gruppen.get(schluessel);
		if (vorhanden) {
			vorhanden.bodenarten.push(name);
			continue;
		}
		gruppen.set(schluessel, {
			gering: t[0],
			mittel: t[1],
			hoch: t[2],
			bodenarten: [name],
			ohneWirkung: t[0] === t[1] && t[1] === t[2],
		});
	}
	return [...gruppen.values()].sort((a, b) => a.gering - b.gering || a.hoch - b.hoch);
}

/** Deutsche Schreibweise: 17.5 → „17,5", 22 → „22". */
export function zahlText(wert: number): string {
	return String(wert).replace('.', ',');
}
